import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import {
  contentEditableToMarkup,
  isAllowedChatLinkHref,
  isSinglePlainUrlForLinkPaste,
  markupToHtml,
  normalizeChatLinkHref,
} from 'components/@home/@messages/ChatPanel/SendForm/controls/ChatField/ChatInput/contentEditableSerializers';
import { findClosestAllowedLink } from 'utils/chatComposerLink';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { arrayOf, func, object, oneOfType, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Socket from 'services/Socket';
import channelShape from 'shapes/channel';
import employeeShape from 'shapes/employee';
import { useLazyListVisibleQuery } from 'store/app/entities/EmployeesApiSlice';
import useDebouncePromise from 'utils/useDebouncePromise';
import connector from './connector';

const debounceTypingActive = throttle(
  (authEmployee, channel, typing) => {
    if (!authEmployee || !channel || typing.current) return;
    const data = { user: authEmployee.user_id, channel: channel._id };
    Socket.emit('typing', { ...data, status: true });

    typing.current = true;
  },
  2000,
  { leading: true, maxWait: 2000, trailing: false },
);

const debounceTypingInactive = debounce(
  (authEmployee, channel, typing) => {
    if (!authEmployee || !channel || !typing.current) return;
    const data = { user: authEmployee.user_id, channel: channel._id };
    Socket.emit('typing', { ...data, status: false });

    typing.current = false;
  },
  2500,
  { leading: false, trailing: true },
);

const Root = styled('div')({
  maxHeight: '50vh',
  padding: '6px 0',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  overflowY: 'auto',
});

const Editable = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: '1.1876em',
  outline: 'none',
  color: theme.typography.body1.color,
  cursor: 'text',
  fontSize: '0.8125rem',
  fontFamily: 'inherit',
  fontWeight: 400,
  lineHeight: 1.3,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  '& strong': { fontWeight: 700 },
  '& em': { fontStyle: 'italic' },
  '& del': { textDecoration: 'line-through' },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.9em',
    padding: '0 2px',
    backgroundColor: theme.palette.action.hover,
    borderRadius: '2px',
  },
  '& a': {
    color: theme.palette.link?.main ?? theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '& h1': { fontSize: '1.1rem', fontWeight: 700, margin: '4px 0 2px' },
  '& h2': { fontSize: '1.05rem', fontWeight: 700, margin: '4px 0 2px' },
  '& h3': { fontSize: '1rem', fontWeight: 700, margin: '3px 0 2px' },
  '& h4, & h5, & h6': { fontSize: '0.95rem', fontWeight: 600, margin: '3px 0 2px' },
  '& [data-mention-id]': {
    padding: '0 2px',
    borderRadius: '3px',
    color: theme.palette.mention,
    backgroundColor: theme.palette.mentionBack,
    display: 'inline-block',
  },
  '&[data-placeholder]:empty::before': {
    content: 'attr(data-placeholder)',
    color: theme.palette.text.secondary,
    opacity: 0.42,
  },
}));

function getTextBeforeCaret(editableEl) {
  const sel = window.getSelection();
  if (!sel.rangeCount || !editableEl.contains(sel.anchorNode)) return '';
  const range = document.createRange();
  range.setStart(editableEl, 0);
  range.setEnd(sel.anchorNode, sel.anchorOffset);
  return range.toString();
}

const INLINE_TAG_MAP = {
  b: 'strong',
  strong: 'strong',
  i: 'em',
  em: 'em',
  s: 'del',
  del: 'del',
  code: 'code',
};

const BLOCK_TAGS = new Set([
  'p',
  'div',
  'li',
  'ul',
  'ol',
  'blockquote',
  'pre',
  'section',
  'article',
]);

const PARAGRAPH_TAGS = new Set(['p', 'blockquote']);

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function appendSanitizedNode(sourceNode, targetParent) {
  if (sourceNode.nodeType === Node.TEXT_NODE) {
    const raw = sourceNode.textContent || '';
    // Whitespace-only text nodes are inter-element formatting (e.g. "\n"
    // between </p> and <p> in source HTML).  Collapse to a single space
    // unless at the start of the output or right after a <br>, where it
    // would become a visible leading indent in pre-wrap.
    if (!raw.trim()) {
      const last = targetParent.lastChild;
      if (!last || last.nodeName === 'BR') return;
      targetParent.appendChild(document.createTextNode(' '));
      return;
    }
    // Collapse whitespace like standard HTML rendering: newlines, tabs,
    // and runs of spaces become a single space.  Intentional line breaks
    // in the source are expressed as <br> or block elements, not as \n
    // characters in text nodes.
    const normalized = raw.replace(/[\r\n\t ]+/g, ' ');
    targetParent.appendChild(document.createTextNode(normalized));
    return;
  }
  if (sourceNode.nodeType !== Node.ELEMENT_NODE) return;

  const tag = (sourceNode.tagName || '').toLowerCase();
  if (tag === 'br') {
    targetParent.appendChild(document.createElement('br'));
    return;
  }

  if (tag === 'a') {
    const hrefRaw = sourceNode.getAttribute && sourceNode.getAttribute('href');
    const href = hrefRaw != null ? String(hrefRaw).trim() : '';
    if (href && isAllowedChatLinkHref(href)) {
      const el = document.createElement('a');
      el.setAttribute('href', href);
      el.setAttribute('rel', 'noopener noreferrer');
      Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, el));
      targetParent.appendChild(el);
      return;
    }
    Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, targetParent));
    return;
  }

  if (HEADING_TAGS.has(tag)) {
    const el = document.createElement(tag);
    Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, el));
    targetParent.appendChild(el);
    return;
  }

  let mappedInline = INLINE_TAG_MAP[tag];

  // Google Docs wraps all clipboard content in <b style="font-weight:normal;">
  // which is not actually bold.  Detect this and similar overrides so we don't
  // wrap the entire paste in <strong>.
  if (mappedInline && sourceNode.style) {
    const fw = sourceNode.style.fontWeight;
    const fs = sourceNode.style.fontStyle;
    const td = sourceNode.style.textDecorationLine || sourceNode.style.textDecoration;
    if (mappedInline === 'strong' && (fw === 'normal' || fw === '400')) mappedInline = null;
    if (mappedInline === 'em' && fs === 'normal') mappedInline = null;
    if (mappedInline === 'del' && td === 'none') mappedInline = null;
  }

  if (mappedInline) {
    const el = document.createElement(mappedInline);
    Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, el));
    targetParent.appendChild(el);
    return;
  }

  if (BLOCK_TAGS.has(tag)) {
    Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, targetParent));
    const hasBlockChild = Array.from(sourceNode.children).some(ch =>
      BLOCK_TAGS.has((ch.tagName || '').toLowerCase()),
    );
    if (!hasBlockChild) {
      targetParent.appendChild(document.createElement('br'));
      if (PARAGRAPH_TAGS.has(tag)) {
        targetParent.appendChild(document.createElement('br'));
      }
    }
    return;
  }

  // Detect inline formatting from styles (Google Docs, Outlook, etc. use
  // styled <span> instead of semantic tags like <b>/<i>/<s>).
  if (sourceNode.style) {
    const fw = sourceNode.style.fontWeight;
    const fs = (sourceNode.style.fontStyle || '').toLowerCase();
    const td = (
      sourceNode.style.textDecorationLine ||
      sourceNode.style.textDecoration ||
      ''
    ).toLowerCase();
    const ff = (sourceNode.style.fontFamily || '').toLowerCase();

    let target = targetParent;
    if (fw === 'bold' || fw === '700' || (fw && Number(fw) >= 600)) {
      const el = document.createElement('strong');
      target.appendChild(el);
      target = el;
    }
    if (fs === 'italic') {
      const el = document.createElement('em');
      target.appendChild(el);
      target = el;
    }
    if (td.includes('line-through')) {
      const el = document.createElement('del');
      target.appendChild(el);
      target = el;
    }
    if (ff.includes('monospace')) {
      const el = document.createElement('code');
      target.appendChild(el);
      target = el;
    }
    if (target !== targetParent) {
      Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, target));
      return;
    }
  }

  // Unknown tags are unwrapped: keep content, drop styling/attributes.
  Array.from(sourceNode.childNodes).forEach(child => appendSanitizedNode(child, targetParent));
}

function sanitizeHtmlToFragment(html) {
  const parser = new DOMParser();
  const parsed = parser.parseFromString(html, 'text/html');
  const fragment = document.createDocumentFragment();
  Array.from(parsed.body.childNodes).forEach(node => appendSanitizedNode(node, fragment));

  // Drop empty inline elements (e.g. Word's <b><o:p>&nbsp;</o:p></b>
  // becomes an empty <strong>) so they don't break <br> run detection.
  for (let n = fragment.firstChild; n;) {
    const next = n.nextSibling;
    if (n.nodeType === Node.ELEMENT_NODE && n.nodeName !== 'BR' && !n.childNodes.length) {
      fragment.removeChild(n);
    }
    n = next;
  }

  // Trim trailing line breaks introduced by block wrappers.
  while (fragment.lastChild && fragment.lastChild.nodeName === 'BR') {
    fragment.removeChild(fragment.lastChild);
  }

  // Limit runs of consecutive <br> to at most 2 (one visible blank line).
  const brsToRemove = [];
  let consecutiveBrs = 0;
  for (let n = fragment.firstChild; n; n = n.nextSibling) {
    if (n.nodeName === 'BR') {
      consecutiveBrs += 1;
      if (consecutiveBrs > 2) brsToRemove.push(n);
    } else {
      consecutiveBrs = 0;
    }
  }
  brsToRemove.forEach(br => fragment.removeChild(br));

  return fragment;
}

function insertFragmentAtCaret(fragment) {
  const sel = window.getSelection();
  if (!sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const lastNode = fragment.lastChild;
  range.insertNode(fragment);
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

function isInsideCode(node) {
  let n = node && (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement);
  while (n) {
    if ((n.tagName || '').toLowerCase() === 'code') return true;
    n = n.parentElement;
  }
  return false;
}

/**
 * Normalize pasted plain text: unify line endings to \n and cap runs of
 * blank lines to at most one (two consecutive \n).  Single newlines are
 * preserved because the plain-text clipboard path is only reached when
 * no HTML is available, meaning the source is explicitly plain text
 * (terminal, text editor, markdown) where line breaks are intentional.
 */
function normalizePastedPlainText(rawText) {
  return rawText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
}

function getFormatState(editableEl) {
  const sel = window.getSelection();
  if (!sel.rangeCount || !editableEl || !editableEl.contains(sel.anchorNode))
    return { bold: false, italic: false, strikethrough: false, code: false, link: false };
  return {
    bold: document.queryCommandState('bold'),
    italic: document.queryCommandState('italic'),
    strikethrough: document.queryCommandState('strikeThrough'),
    code: isInsideCode(sel.anchorNode),
    link: !!findClosestAllowedLink(sel.anchorNode, editableEl),
  };
}

function insertMentionAtCaret(editableEl, mention) {
  const sel = window.getSelection();
  if (!sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  const rawDisplay = String(mention?.display ?? '');
  const normalizedDisplay = rawDisplay.replace(/^@+/, '').trim();
  if (!normalizedDisplay) return;

  // Replace the current "@query" token before caret to avoid "@@mention".
  if (range.startContainer?.nodeType === Node.TEXT_NODE) {
    const textNode = range.startContainer;
    const textBeforeCaret = (textNode.textContent || '').slice(0, range.startOffset);
    const tokenMatch = /(^|\s)@([^\s@]*)$/.exec(textBeforeCaret);
    if (tokenMatch) {
      const tokenLength = tokenMatch[0].startsWith(' ')
        ? tokenMatch[0].length - 1
        : tokenMatch[0].length;
      const deleteStart = Math.max(0, range.startOffset - tokenLength);
      const deleteRange = document.createRange();
      deleteRange.setStart(textNode, deleteStart);
      deleteRange.setEnd(textNode, range.startOffset);
      deleteRange.deleteContents();
      range.setStart(textNode, deleteStart);
      range.collapse(true);
    }
  }

  const span = document.createElement('span');
  span.setAttribute('data-mention-id', mention.id);
  span.setAttribute('data-mention-display', normalizedDisplay);
  span.setAttribute('contenteditable', 'false');
  span.textContent = `@${normalizedDisplay}`;
  range.deleteContents();
  range.insertNode(span);
  range.setStartAfter(span);
  range.setEndAfter(span);
  sel.removeAllRanges();
  sel.addRange(range);
}

const ChatInput = ({
  authEmployee = null,
  channel,
  onKeyPress,
  name,
  onChange,
  onFormatStateChange,
  onLinkShortcut,
  onLinkEdit,
  suggestionsPortal,
  onPaste,
  placeholder,
  value,
  inputRef = () => {},
}) => {
  const typing = useRef(false);
  const editableRef = useRef(null);
  const lastEmittedMarkup = useRef(null);
  const lastChannelId = useRef(null);
  const syncRequestId = useRef(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);

  const [triggerListVisibleQuery] = useLazyListVisibleQuery();
  const listVisibleQuery = useDebouncePromise(
    useCallback(
      (search, cb) =>
        triggerListVisibleQuery({ search })
          .unwrap()
          .then(result => {
            cb(
              result.employees
                .filter(e => e.user?._id)
                .map(e => {
                  const display = [e.firstName, e.surName].filter(n => n).join(' ');
                  return {
                    ...e,
                    id: `${e._id}-${e.user?._id || ''}`,
                    name: display,
                    user: e.user,
                    display,
                  };
                }),
            );
          }),
      [triggerListVisibleQuery],
    ),
    300,
  );

  const emitChange = useCallback(
    el => {
      if (!el) return;
      const { markup, text, mentions } = contentEditableToMarkup(el);
      lastEmittedMarkup.current = markup;
      onChange({
        target: {
          name,
          value: { markup, text, mentions },
        },
      });
    },
    [name, onChange],
  );

  const syncFromValue = useCallback(
    (markup, mentions, force = false) => {
      const el = editableRef.current;
      if (!el) return;
      const isClear = !markup;
      const channelChanged = channel?._id !== lastChannelId.current;
      const isExternalUpdate =
        lastEmittedMarkup.current !== null && lastEmittedMarkup.current !== markup;
      if (isClear) {
        el.innerHTML = '';
        lastChannelId.current = channel?._id;
        lastEmittedMarkup.current = '';
        return;
      }
      if (force || channelChanged || lastEmittedMarkup.current === null || isExternalUpdate) {
        const requestId = ++syncRequestId.current;
        markupToHtml(markup, mentions || []).then(html => {
          if (requestId !== syncRequestId.current || editableRef.current !== el) return;
          el.innerHTML = html;
          lastChannelId.current = channel?._id;
          lastEmittedMarkup.current = markup;
        });
      }
    },
    [channel?._id],
  );

  useEffect(() => {
    const markup = value?.markup ?? '';
    const mentions = value?.mentions ?? [];
    syncFromValue(markup, mentions);
  }, [value?.markup, value?.mentions, syncFromValue]);

  const setEditableRef = useCallback(
    el => {
      editableRef.current = el;
      if (typeof inputRef === 'function') inputRef(el);
      else if (inputRef && typeof inputRef === 'object') inputRef.current = el;
    },
    [inputRef],
  );

  useEffect(() => {
    if (editableRef.current && channel?._id) {
      setTimeout(() => editableRef.current?.focus(), 100);
    }
  }, [channel?._id]);

  useEffect(() => {
    if (!onFormatStateChange) return () => {};
    const report = () => {
      const el = editableRef.current;
      if (el) onFormatStateChange(getFormatState(el));
    };
    document.addEventListener('selectionchange', report);
    return () => document.removeEventListener('selectionchange', report);
  }, [onFormatStateChange]);

  const handleInput = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    emitChange(el);

    const before = getTextBeforeCaret(el);
    const match = /@(\S*)$/.exec(before);
    if (match) {
      const query = match[1];
      setMentionQuery(query);
      listVisibleQuery(query, suggestions => {
        setMentionSuggestions(suggestions);
        setMentionSelectedIndex(0);
      });
    } else {
      setMentionSuggestions([]);
    }
  }, [emitChange, listVisibleQuery]);

  const handleKeyDown = useCallback(
    e => {
      debounceTypingActive(authEmployee, channel, typing);
      debounceTypingInactive(authEmployee, channel, typing);

      if (mentionSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionSelectedIndex(i => (i + 1) % mentionSuggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionSelectedIndex(
            i => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length,
          );
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const suggestion = mentionSuggestions[mentionSelectedIndex];
          if (suggestion && editableRef.current) {
            insertMentionAtCaret(editableRef.current, suggestion);
            setMentionSuggestions([]);
            emitChange(editableRef.current);
          }
          return;
        }
        if (e.key === 'Escape') {
          setMentionSuggestions([]);
          return;
        }
      }

      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        onLinkShortcut?.();
        return;
      }

      onKeyPress(e);
    },
    [
      authEmployee,
      channel,
      mentionSuggestions,
      mentionSelectedIndex,
      onKeyPress,
      onLinkShortcut,
      emitChange,
    ],
  );

  const handleEditableClick = useCallback(
    e => {
      const ed = editableRef.current;
      if (!ed || !onLinkEdit) return;
      const linkNode = findClosestAllowedLink(e.target, ed);
      if (linkNode) {
        e.preventDefault();
        onLinkEdit(linkNode);
      }
    },
    [onLinkEdit],
  );

  const handlePaste = useCallback(
    e => {
      onPaste(e);
      const clipboardData = e.clipboardData;
      const hasImage = Array.from(clipboardData?.files || []).some(file =>
        String(file?.type || '').startsWith('image/'),
      );

      const html = clipboardData?.getData('text/html');
      const text = clipboardData?.getData('text/plain');
      const el = editableRef.current;
      const sel = window.getSelection();

      if (
        !hasImage &&
        el &&
        sel.rangeCount &&
        el.contains(sel.anchorNode) &&
        !sel.getRangeAt(0).collapsed &&
        typeof text === 'string' &&
        isSinglePlainUrlForLinkPaste(text)
      ) {
        e.preventDefault();
        el.focus();
        const href = normalizeChatLinkHref(text.trim());
        document.execCommand('createLink', false, href);
        const created = findClosestAllowedLink(sel.anchorNode, el);
        if (created) created.setAttribute('rel', 'noopener noreferrer');
        setTimeout(() => editableRef.current && emitChange(editableRef.current), 0);
        return;
      }

      if (hasImage && !html) {
        // Pure image paste — let the clipboard image dialog handle it.
        e.preventDefault();
      } else if (html) {
        e.preventDefault();
        const fragment = sanitizeHtmlToFragment(html);
        insertFragmentAtCaret(fragment);
      } else if (typeof text === 'string') {
        e.preventDefault();
        document.execCommand('insertText', false, normalizePastedPlainText(text));
      }

      if (!e.defaultPrevented) {
        setTimeout(() => editableRef.current && emitChange(editableRef.current), 0);
        return;
      }

      // For handled paste paths (sanitized text/image), still sync form state.
      setTimeout(() => editableRef.current && emitChange(editableRef.current), 0);
    },
    [onPaste, emitChange],
  );

  const isVisible = (ele, container) => {
    if (!ele || !container) return false;
    const eleTop = ele.offsetTop;
    const eleBottom = eleTop + ele.clientHeight;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    return eleTop >= containerTop && eleBottom <= containerBottom;
  };

  const renderUserSuggestion = (suggestion, index, focused) => {
    let elemRef;
    setTimeout(() => {
      if (elemRef && focused && suggestionsPortal?.current) {
        const visible = isVisible(elemRef, suggestionsPortal.current);
        if (!visible) elemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 10);
    return (
      <Box
        key={suggestion.id}
        ref={r => {
          elemRef = r;
        }}
        role="button"
        tabIndex={-1}
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          padding: '5px 10px',
          background: focused ? theme.palette.primary.extraLight : theme.palette.background.paper,
        })}
        onMouseDown={e => {
          e.preventDefault();
          if (editableRef.current) {
            insertMentionAtCaret(editableRef.current, suggestion);
            setMentionSuggestions([]);
            emitChange(editableRef.current);
            editableRef.current.focus();
          }
        }}
      >
        <EmployeeAvatar employee={suggestion} />
        <Typography sx={{ ml: 1.25 }}>{suggestion.name}</Typography>
      </Box>
    );
  };

  const showMentions = mentionSuggestions.length > 0 && mentionQuery !== undefined;
  const mentionPortal =
    suggestionsPortal?.current && showMentions
      ? ReactDOM.createPortal(
          <div
            style={{
              position: 'relative',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              maxHeight: 200,
              overflowY: 'auto',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {mentionSuggestions.map((s, i) =>
              renderUserSuggestion(s, i, i === mentionSelectedIndex),
            )}
          </div>,
          suggestionsPortal.current,
        )
      : null;

  return (
    <Root>
      <Editable
        ref={setEditableRef}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleEditableClick}
        onBlur={() => editableRef.current && emitChange(editableRef.current)}
      />
      {mentionPortal}
    </Root>
  );
};

ChatInput.propTypes = {
  placeholder: string.isRequired,
  name: string.isRequired,
  value: shape({
    markup: string,
    text: string,
    mentions: arrayOf(object),
  }),
  authEmployee: employeeShape,
  channel: channelShape.isRequired,
  onChange: func.isRequired,
  onFormatStateChange: func,
  onLinkShortcut: func,
  onLinkEdit: func,
  onPaste: func.isRequired,
  onKeyPress: func.isRequired,
  inputRef: oneOfType([func, object]),
  suggestionsPortal: object.isRequired,
};

export default connector(ChatInput);
